import { FormEvent, useState } from "react";
import { FieldValues, useFormState } from "react-hook-form";
import { Drawer } from "@mui/material";

import Button, { ButtonTypes } from "../Button";
import { MobilePanelHeader } from "../MobileForm";
import { FormContents, MobileBody, MobileScreen } from "../MobileForm/styles";
import { DiscardPopUp } from "../PopUp";
import { SidePanel } from "../SidePanel";
import { useResponsive } from "../../hooks/useResponsive";
import { BREAKPOINTS } from "../../utils/constants";
import { PanelState } from "./PanelState";
import {
  ButtonsWrapper,
  CardsForm,
  FooterNote,
  FooterStack,
  HeaderEyebrow,
  HeaderTitle,
  PanelBanner,
  PanelHeader,
} from "./styles";
import { FormPanelProps } from "./types";

const DISCARD_TEXT = "Your unsaved edits will be lost. You can't undo this.";

export const FormPanel = <TValues extends FieldValues>({
  open,
  onClose,
  ariaLabel,
  eyebrow,
  title,
  form,
  resetValues,
  formId,
  onSubmit,
  submitLabel,
  isSubmitDisabled = false,
  error,
  discard,
  mobile,
  footerNote,
  load,
  children,
}: FormPanelProps<TValues>) => {
  // MUI aria-hides #modal-root while the drawer is open, so the confirmation portals into the drawer root.
  const [drawerRoot, setDrawerRoot] = useState<HTMLDivElement | null>(null);
  const [isDiscardOpen, setIsDiscardOpen] = useState(false);
  const isCompact = useResponsive(BREAKPOINTS.tabletLarge);
  const { isDirty, isSubmitting } = useFormState({ control: form.control });

  const isReady = !load || load.status === "ready";
  const submitValues = form.handleSubmit(onSubmit);

  const submit = (event: FormEvent<HTMLFormElement>) => {
    if (isSubmitting || isSubmitDisabled) {
      event.preventDefault();
      return;
    }
    // Handlers report their own failures; this only keeps RHF's rethrow from being an unhandled rejection.
    submitValues(event).catch(() => {});
  };

  const closeAndReset = () => {
    form.reset(resetValues);
    onClose();
  };

  const requestClose = () => {
    if (isSubmitting) {
      return;
    }
    if (isDirty) {
      setIsDiscardOpen(true);
      return;
    }
    closeAndReset();
  };

  const handleDiscard = () => {
    setIsDiscardOpen(false);
    closeAndReset();
  };

  const discardDetails = isDiscardOpen ? discard.details() : undefined;

  const discardDialog = (
    <DiscardPopUp
      portalContainer={drawerRoot}
      handleOnDiscard={handleDiscard}
      isOpen={isDiscardOpen}
      setIsOpen={setIsDiscardOpen}
      eyebrow={eyebrow}
      title={discard.title}
      text={DISCARD_TEXT}
      details={discardDetails}
      confirmLabel={discard.confirmLabel}
    />
  );

  const stateBody = load ? <PanelState {...load} /> : null;
  const banner = error ? <PanelBanner role="alert">{error}</PanelBanner> : null;
  const note = footerNote ? <FooterNote>{footerNote}</FooterNote> : null;

  if (isCompact) {
    const mobileBody = isReady ? (
      <FormContents id={formId} onSubmit={submit}>
        <MobileBody>
          {banner}
          {mobile.fields}
          {note}
        </MobileBody>
      </FormContents>
    ) : (
      <MobileBody>{stateBody}</MobileBody>
    );

    return (
      <>
        <Drawer
          ref={setDrawerRoot}
          open={open}
          onClose={requestClose}
          anchor="right"
          disableEnforceFocus={isDiscardOpen}
          slotProps={{
            paper: {
              "aria-label": ariaLabel,
              sx: { width: "100vw", maxWidth: "100vw" },
            },
          }}
        >
          <MobileScreen>
            <MobilePanelHeader
              title={mobile.title}
              actionLabel={mobile.submitLabel}
              isSubmitting={isSubmitting}
              isActionDisabled={!isReady || isSubmitDisabled}
              formId={formId}
              onCancel={requestClose}
            />
            {mobileBody}
          </MobileScreen>
        </Drawer>
        {discardDialog}
      </>
    );
  }

  const footer = (
    <FooterStack>
      {note}
      <ButtonsWrapper>
        <Button
          type={ButtonTypes.cancel}
          onClick={requestClose}
          disabled={isSubmitting}
        >
          Cancel
        </Button>
        <Button
          buttonType="submit"
          type={ButtonTypes.primary}
          form={formId}
          disabled={isSubmitting || isSubmitDisabled}
        >
          {isSubmitting ? "Saving…" : submitLabel}
        </Button>
      </ButtonsWrapper>
    </FooterStack>
  );

  const titleBar = (
    <PanelHeader>
      <HeaderEyebrow>{eyebrow}</HeaderEyebrow>
      <HeaderTitle>{title}</HeaderTitle>
    </PanelHeader>
  );

  const panelFooter = isReady ? footer : undefined;

  const desktopBody = isReady ? (
    <CardsForm id={formId} onSubmit={submit}>
      {banner}
      {children}
    </CardsForm>
  ) : (
    stateBody
  );

  return (
    <>
      <SidePanel
        open={open}
        onClose={requestClose}
        ariaLabel={ariaLabel}
        rootRef={setDrawerRoot}
        disableEnforceFocus={isDiscardOpen}
        closeIconPosition="trailing"
        isCloseDisabled={isSubmitting}
        titleBar={titleBar}
        footer={panelFooter}
      >
        {desktopBody}
      </SidePanel>
      {discardDialog}
    </>
  );
};
